import React from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';

import type { StudioParameterPalette } from '../../../domain';
import { resolveActiveSwatch, toggleSwatchSelection, type SwatchSelections } from './swatchSelection';
import { paletteKindIcon } from './paletteIcons';

interface Props {
  palettes: StudioParameterPalette[];
  selections: SwatchSelections;
  onSelectionsChange: (next: SwatchSelections) => void;
}

/**
 * Dismissible summary of the active swatch picks, shown above the operation
 * panel (e.g. "Photoreal × Wide 16:9 × Moody"). Each chip carries its palette
 * glyph and an ✕ that toggles that palette's selection off — turning the
 * otherwise-buried swatch state into something visible and directly editable
 * from where the user is actually working.
 *
 * Renders nothing when no swatch is active, so the panel layout collapses
 * cleanly.
 */
export const SelectionChips: React.FC<Props> = ({ palettes, selections, onSelectionsChange }) => {
  const active = palettes
    .map(palette => ({ palette, swatch: resolveActiveSwatch(palette, selections) }))
    .filter((x): x is { palette: StudioParameterPalette; swatch: NonNullable<typeof x.swatch> } => !!x.swatch);

  if (active.length === 0) return null;

  return (
    <Box sx={{ px: 1.5, py: 1 }} aria-label="Active swatch selections">
      <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center" sx={{ rowGap: 0.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.25 }}>
          Selections:
        </Typography>
        {active.map(({ palette, swatch }) => {
          const Icon = paletteKindIcon(palette.kind);
          return (
            <Chip
              key={palette.id}
              size="small"
              variant="outlined"
              icon={<Icon sx={{ fontSize: 14 }} />}
              label={swatch.label}
              onDelete={() => onSelectionsChange(toggleSwatchSelection(selections, palette.id, swatch.id))}
              sx={{ '& .MuiChip-icon': { ml: 0.5 } }}
            />
          );
        })}
      </Stack>
    </Box>
  );
};
